import { fetchTokenByRefresh } from '@api/auth/auth';
import { HttpError, isUnauthorizedError } from '@api/types';
import { LocalStorageKeys, SessionStorageKeys } from '@commons/consts';
import { logException } from '@configs/azureAppInsights/utils';
import { shouldRefreshToken } from '@utils/auth';
import { useCallback } from 'react';

// A dashboard page fires many queries at once, so a single expired token
// produces several near-simultaneous 401s. Without this module-level guard,
// each one would independently start its own refresh-token request and its
// own window.location.reload(), racing each other and reloading the page
// multiple times before it settled.
let isHandlingUnauthorizedError = false;

export const usePageRefreshOnUnauthorizedError = () => {
  const refreshPage = useCallback((error: HttpError | Error) => {
    if (!isUnauthorizedError(error)) {
      return;
    }

    if (isHandlingUnauthorizedError) {
      return;
    }
    isHandlingUnauthorizedError = true;

    if (!shouldRefreshToken()) {
      // Retries already exhausted for this session - do nothing further.
      // Reloading here would just send the user back into the same
      // redirect -> token -> 401 cycle with no way to break out of it.
      return;
    }

    const isMobile =
      sessionStorage.getItem(SessionStorageKeys.isMobile) === 'true';
    if (isMobile) {
      const refreshToken =
        localStorage.getItem(LocalStorageKeys.refreshToken) ?? '';
      fetchTokenByRefresh(refreshToken)
        .then(response => {
          localStorage.setItem(LocalStorageKeys.idToken, response.id_token);
          localStorage.setItem(
            LocalStorageKeys.refreshToken,
            response.refresh_token,
          );
          sessionStorage.setItem(
            SessionStorageKeys.token,
            response.access_token,
          );
          sessionStorage.removeItem(SessionStorageKeys.tokenRefreshingAttempts);
          window.location.reload();
        })
        .catch(e => {
          logException(`Error while fetching token by refresh token. ${e}`);
          sessionStorage.removeItem(SessionStorageKeys.token);
          window.location.reload();
        });
    } else {
      sessionStorage.removeItem(SessionStorageKeys.token);
      window.location.reload();
    }
  }, []);

  return { refreshPage };
};
